import { FC, Children, cloneElement, createContext, memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import Checkbox from '../Checkbox';
import {
  CheckboxOptionType,
  CheckboxValueType,
  CheckboxGroupContext,
  CheckboxGroupProps,
} from '../types';

export const GroupContext = createContext<CheckboxGroupContext | null>(null);

const CheckboxGroup: FC<CheckboxGroupProps> = ({
  defaultValue,
  children,
  options = [],
  prefixCls = 'checkbox',
  className,
  style,
  onChange,
  ...restProps
}) => {
  const [value, setValue] = useState<CheckboxValueType[]>(restProps.value || defaultValue || []);
  const [registeredValues, setRegisteredValues] = useState<CheckboxValueType[]>([]);

  useEffect(() => {
    if ('value' in restProps) {
      setValue(restProps.value || []);
    }
  }, [restProps]);

  const getOptions = () =>
    options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        };
      }
      return option;
    });

  const cancelValue = (val: string) => {
    setRegisteredValues((prevValues) => prevValues.filter((v) => v !== val));
  };

  const registerValue = (val: string) => {
    setRegisteredValues((prevValues) => [...prevValues, val]);
  };

  const toggleOption = (option: CheckboxOptionType) => {
    const optionIndex = value.indexOf(option.value);
    const newValue = [...value];
    if (optionIndex === -1) {
      newValue.push(option.value);
    } else {
      newValue.splice(optionIndex, 1);
    }
    if (!('value' in restProps)) {
      setValue(newValue);
    }
    const opts = getOptions();
    onChange?.(
      newValue
        .filter((val) => registeredValues.indexOf(val) !== -1)
        .sort((a, b) => {
          const indexA = opts.findIndex((opt) => opt.value === a);
          const indexB = opts.findIndex((opt) => opt.value === b);
          return indexA - indexB;
        }),
    );
  };

  const groupPrefixCls = `${prefixCls}-group`;

  const domProps = omit(restProps, ['value', 'disabled']);

  if (options && options.length > 0) {
    children = getOptions().map((option) => (
      <Checkbox
        prefixCls={prefixCls}
        key={option.value?.toString()}
        disabled={'disabled' in option ? option.disabled : restProps.disabled}
        value={option.value}
        checked={value.indexOf(option.value) !== -1}
        onChange={option.onChange}
        className={`${groupPrefixCls}-item`}
        style={option.style}
      >
        {option.label}
      </Checkbox>
    ));
  }

  const context = {
    toggleOption,
    value,
    disabled: restProps.disabled,
    name: restProps.name,

    // https://github.com/ant-design/ant-design/issues/16376
    registerValue,
    cancelValue,
  };

  const classString = classNames(groupPrefixCls, className);

  return (
    <div className={classString} style={style} {...domProps}>
      <GroupContext.Provider value={context}>
        {Children.map(children as React.ReactElement, (child: React.ReactElement) =>
          cloneElement(child, { value, toggleOption }),
        )}
      </GroupContext.Provider>
    </div>
  );
};

export default memo(CheckboxGroup);
